import { ResizePanel } from '@affine/component/resize-panel';
import {
  appendPersistentMemory,
  getTranscriptionApiKey,
  streamGroqToAffineUI,
} from '@affine/core/blocksuite/ai/cao-oi-gemini-bridge';
import { AffineErrorComponent } from '@affine/core/components/affine/affine-error-boundary/affine-error-fallback';
import { GlobalVoicePillHeader } from '@affine/core/desktop/pages/workspace/recordings/global-voice-pill-header';
import { workbenchRoutes } from '@affine/core/desktop/workbench-router';
import { DocService } from '@affine/core/modules/doc';
import { EditorService } from '@affine/core/modules/editor';
import {
  appSettingAtom,
  FrameworkScope,
  useLiveData,
  useService,
  useServiceOptional,
} from '@toeverything/infra';
import { useAtomValue } from 'jotai';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { type RouteObject, useLocation } from 'react-router-dom';

import type { View } from '../entities/view';
import { WorkbenchService } from '../services/workbench';
import { useBindWorkbenchToBrowserRouter } from './browser-adapter';
import { useBindWorkbenchToDesktopRouter } from './desktop-adapter';
import { RouteContainer } from './route-container';
import { SidebarContainer } from './sidebar/sidebar-container';
import { SplitView } from './split-view/split-view';
import { ViewIslandRegistryProvider } from './view-islands';
import { ViewRoot } from './view-root';
import * as styles from './workbench-root.css';

const useAdapter = BUILD_CONFIG.isElectron
  ? useBindWorkbenchToDesktopRouter
  : useBindWorkbenchToBrowserRouter;

const routes: RouteObject[] = [
  {
    element: <RouteContainer />,
    errorElement: <AffineErrorComponent />,
    children: workbenchRoutes,
  },
];

// --- React Bits Styles & Keyframes ---
const REACT_BITS_INJECTED_STYLES = `
@keyframes rbShine {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
@keyframes rbPulseRing {
  0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
  70% { transform: scale(1); box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
}
@keyframes rbWaveBar1 { 0%, 100% { height: 4px; } 50% { height: 18px; } }
@keyframes rbWaveBar2 { 0%, 100% { height: 14px; } 50% { height: 6px; } }
@keyframes rbWaveBar3 { 0%, 100% { height: 8px; } 50% { height: 22px; } }
@keyframes rbWaveBar4 { 0%, 100% { height: 18px; } 50% { height: 8px; } }
@keyframes rbBorderGlow {
  0% { filter: hue-rotate(0deg); }
  100% { filter: hue-rotate(360deg); }
}
`;

export const CaoOiAudioDock = () => {
  const editorService = useServiceOptional(EditorService);
  const docService = useServiceOptional(DocService);

  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [lastAudioUrl, setLastAudioUrl] = useState<string | null>(null);
  const [lastAudioSize, setLastAudioSize] = useState<string>('');
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiQuestion, setAiQuestion] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [isAiStreaming, setIsAiStreaming] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string>('');

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunkTimerRef = useRef<number | null>(null);
  const secondTimerRef = useRef<number | null>(null);
  const allRecordedChunksRef = useRef<BlobPart[]>([]);

  // Insert styles once
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!document.getElementById('react-bits-dock-styles')) {
      const el = document.createElement('style');
      el.id = 'react-bits-dock-styles';
      el.innerHTML = REACT_BITS_INJECTED_STYLES;
      document.head.append(el);
    }
  }, []);

  const insertIntoCanvasAndMemory = (text: string) => {
    const time = new Date().toLocaleTimeString('vi-VN', {
      hour: '2-digit',
      minute: '2-digit',
    });
    const entry = `🎙️ [${time}] ${text}`;

    // 1. Save to AI Persistent Memory
    appendPersistentMemory(`Lời giảng viên: ${text}`);

    // 2. Drop directly onto the active AFFiNE page / Edgeless note
    try {
      const host =
        (typeof document !== 'undefined' &&
          (document.querySelector('editor-host') as any)) ||
        (typeof document !== 'undefined' &&
          (document.querySelector('affine-edgeless-root') as any));
      const doc =
        editorService?.editor?.doc?.blockSuiteDoc ||
        (docService as any)?.doc?.blockSuiteDoc ||
        host?.doc;

      if (doc) {
        const notes = doc.getBlocksByFlavour('affine:note');
        if (notes[0]?.id) {
          doc.addBlock(
            'affine:paragraph',
            { type: 'text', text: new doc.Text(entry) },
            notes[0].id
          );
        }
      }
    } catch (e) {
      console.warn('[CaoOi] Note insertion skipped:', e);
    }
  };

  const transcribeAudioChunk = async (blob: Blob, ext = 'webm') => {
    if (blob.size < 1500) return;
    setIsTranscribing(true);
    setStatusMessage('Đang dịch (Groq)...');

    const groqKey = getTranscriptionApiKey();
    if (!groqKey) {
      insertIntoCanvasAndMemory('[Lỗi]: Chưa có Groq API key để phiên âm.');
      setIsTranscribing(false);
      setStatusMessage('');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', blob, `audio.${ext}`);
      formData.append('model', 'whisper-large-v3');
      formData.append('language', 'vi');
      formData.append(
        'prompt',
        'Chuyển đoạn ghi âm bài giảng CEO/Quản trị này sang tiếng Việt chuẩn xác. Giữ đúng các thuật ngữ: Situational Leadership, Push & Pull, FACT Feedback, C-V-P, SMART, Storyselling. Chỉ trả về lời nói nghe được.'
      );

      const res = await fetch(
        'https://api.groq.com/openai/v1/audio/transcriptions',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${groqKey}`,
          },
          body: formData,
        }
      );

      if (res.ok) {
        const data = await res.json();
        const transcript = data?.text?.trim();
        if (transcript) {
          insertIntoCanvasAndMemory(transcript);
        }
      } else {
        const err = await res.text();
        console.error('Groq Whisper error:', err);
      }
    } catch (e) {
      console.error('Groq transcription failed:', e);
    } finally {
      setIsTranscribing(false);
      setStatusMessage('');
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      let mimeType = 'audio/webm';
      let fileExt = 'webm';
      if (typeof MediaRecorder !== 'undefined') {
        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
          mimeType = 'audio/webm;codecs=opus';
          fileExt = 'webm';
        } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4';
          fileExt = 'mp4';
        } else if (MediaRecorder.isTypeSupported('audio/webm')) {
          mimeType = 'audio/webm';
          fileExt = 'webm';
        } else if (MediaRecorder.isTypeSupported('audio/aac')) {
          mimeType = 'audio/aac';
          fileExt = 'm4a';
        }
      }

      const recorder = new MediaRecorder(stream, { mimeType });
      recorderRef.current = recorder;
      allRecordedChunksRef.current = [];
      let currentChunk: BlobPart[] = [];

      recorder.ondataavailable = e => {
        if (e.data.size > 0) {
          currentChunk.push(e.data);
          allRecordedChunksRef.current.push(e.data);
        }
      };

      recorder.onstop = () => {
        if (currentChunk.length > 0) {
          const sliceBlob = new Blob(currentChunk, { type: mimeType });
          currentChunk = [];
          transcribeAudioChunk(sliceBlob, fileExt).catch(console.error);
        }
      };

      recorder.start();
      setIsRecording(true);
      setRecordSeconds(0);

      // Duration counter with 90-minute safety cap to prevent battery drain
      secondTimerRef.current = window.setInterval(() => {
        setRecordSeconds(s => {
          if (s >= 5400) {
            stopRecording();
            return s;
          }
          return s + 1;
        });
      }, 1000);

      // 20-second continuous chunking loop
      chunkTimerRef.current = window.setInterval(() => {
        if (recorder.state === 'recording') {
          recorder.stop();
          recorder.start();
        }
      }, 20000);
    } catch (err) {
      alert(
        'Không thể mở Microphone. Vui lòng cấp quyền Microphone trên trình duyệt!'
      );
      console.error(err);
    }
  };

  const stopRecording = () => {
    if (secondTimerRef.current) window.clearInterval(secondTimerRef.current);
    if (chunkTimerRef.current) window.clearInterval(chunkTimerRef.current);

    if (recorderRef.current) {
      try {
        if (recorderRef.current.state !== 'inactive') {
          recorderRef.current.stop();
        }
      } catch {}
      recorderRef.current.stream.getTracks().forEach(t => t.stop());
    }

    setIsRecording(false);

    // Build complete downloadable audio file from all accumulated chunks
    setTimeout(() => {
      if (allRecordedChunksRef.current.length > 0) {
        const fullBlob = new Blob(allRecordedChunksRef.current, {
          type: 'audio/webm',
        });
        const url = URL.createObjectURL(fullBlob);
        setLastAudioUrl(url);
        const sizeMb = (fullBlob.size / (1024 * 1024)).toFixed(1);
        setLastAudioSize(`${sizeMb} MB`);
      }
    }, 500);
  };

  // Battery Safety: Stop continuous lecture recording if the tab is in background for over 5 minutes
  useEffect(() => {
    let backgroundTimeout: ReturnType<typeof setTimeout> | null = null;
    const handleVis = () => {
      if (document.hidden) {
        backgroundTimeout = setTimeout(() => {
          if (isRecording) {
            stopRecording();
          }
        }, 300000);
      } else {
        if (backgroundTimeout) {
          clearTimeout(backgroundTimeout);
          backgroundTimeout = null;
        }
      }
    };
    document.addEventListener('visibilitychange', handleVis);
    const handleBeforeUnload = () => {
      stopRecording();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVis);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (backgroundTimeout) clearTimeout(backgroundTimeout);
    };
  }, [isRecording]);

  const handleAskGroq = async (customPrompt?: string) => {
    const q = (customPrompt || aiQuestion).trim();
    if (!q || isAiStreaming) return;
    setIsAiStreaming(true);
    setAiResponse('');
    try {
      let accumulated = '';
      for await (const chunk of streamGroqToAffineUI(q)) {
        accumulated += chunk;
        setAiResponse(accumulated);
      }
    } catch (e: any) {
      setAiResponse(`⚠️ Lỗi Groq: ${e?.message || e}`);
    } finally {
      setIsAiStreaming(false);
    }
  };

  const insertAiAnswerToDoc = () => {
    if (!aiResponse) return;
    insertIntoCanvasAndMemory(`[Cáo Ơi]: ${aiResponse}`);
    setStatusMessage('Đã thêm câu trả lời vào bài học!');
    setTimeout(() => setStatusMessage(''), 3000);
  };

  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60)
      .toString()
      .padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <>
      {/* --- React Bits AI Modal (Floating Glassmorphism Card) --- */}
      {showAiModal && (
        <div
          style={{
            position: 'fixed',
            bottom: 84,
            right: 76,
            width: 440,
            maxHeight: 520,
            background: 'rgba(15, 17, 23, 0.95)',
            backdropFilter: 'blur(24px) saturate(180%)',
            WebkitBackdropFilter: 'blur(24px) saturate(180%)',
            borderRadius: 20,
            border: '1px solid rgba(168, 85, 247, 0.35)',
            boxShadow:
              '0 24px 60px rgba(0,0,0,0.7), 0 0 30px rgba(168, 85, 247, 0.2), inset 0 1px 1px rgba(255,255,255,0.15)',
            zIndex: 10000,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            fontFamily: 'Inter, system-ui, sans-serif',
            color: '#f4f4f5',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '14px 18px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, transparent 100%)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#f4f4f5',
                }}
              >
                Cáo Ơi
              </div>
            </div>
            <button
              onClick={() => setShowAiModal(false)}
              style={{
                background: 'rgba(255, 255, 255, 0.08)',
                border: 'none',
                color: '#94a3b8',
                borderRadius: 999,
                width: 26,
                height: 26,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
              }}
            >
              ✕
            </button>
          </div>

          {/* Quick Prompts */}
          <div
            style={{
              display: 'flex',
              gap: 6,
              padding: '10px 14px',
              overflowX: 'auto',
              borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
              background: 'rgba(0, 0, 0, 0.2)',
            }}
          >
            {[
              '📋 Tóm tắt 3 ý chính vừa học',
              '🎯 Trích xuất Action Items',
              '💡 Giải thích thuật ngữ',
            ].map(prompt => (
              <button
                key={prompt}
                onClick={() => {
                  setAiQuestion(prompt);
                  handleAskGroq(prompt).catch(console.error);
                }}
                style={{
                  background: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 999,
                  padding: '4px 10px',
                  color: '#cbd5e1',
                  fontSize: 11,
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                {prompt}
              </button>
            ))}
          </div>

          {/* Response Box */}
          <div
            style={{
              padding: 16,
              flex: 1,
              overflowY: 'auto',
              minHeight: 160,
              fontSize: 13,
              lineHeight: 1.6,
              color: '#e2e8f0',
              whiteSpace: 'pre-wrap',
            }}
          >
            {aiResponse ? (
              aiResponse
            ) : isAiStreaming ? (
              <span style={{ color: '#a855f7' }}>✨ Cáo Ơi đang phân tích nội dung bài giảng...</span>
            ) : (
              <span style={{ color: '#64748b' }}>
                Hỏi Cáo Ơi bất cứ điều gì về bài giảng vừa ghi âm (VD: &quot;Chị Quyên cần chú ý gì về Situational Leadership?&quot;).
              </span>
            )}
          </div>

          {/* Action Toolbar & Input */}
          <div
            style={{
              padding: 12,
              borderTop: '1px solid rgba(255, 255, 255, 0.08)',
              background: 'rgba(0, 0, 0, 0.3)',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            {aiResponse && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                <button
                  onClick={insertAiAnswerToDoc}
                  style={{
                    background: 'rgba(168, 85, 247, 0.2)',
                    border: '1px solid rgba(168, 85, 247, 0.4)',
                    color: '#e9d5ff',
                    borderRadius: 8,
                    padding: '4px 10px',
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  📝 Chèn câu trả lời vào Bảng
                </button>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                placeholder="Nhập câu hỏi cho Cáo Ơi..."
                value={aiQuestion}
                onChange={e => setAiQuestion(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    handleAskGroq().catch(console.error);
                  }
                }}
                style={{
                  flex: 1,
                  background: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(255, 255, 255, 0.14)',
                  borderRadius: 10,
                  padding: '8px 12px',
                  color: '#fff',
                  fontSize: 13,
                  outline: 'none',
                }}
              />
              <button
                onClick={() => {
                  handleAskGroq().catch(console.error);
                }}
                disabled={isAiStreaming}
                style={{
                  background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                  border: 'none',
                  borderRadius: 10,
                  padding: '8px 16px',
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: isAiStreaming ? 'not-allowed' : 'pointer',
                  opacity: isAiStreaming ? 0.6 : 1,
                }}
              >
                Gửi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- React Bits Floating Dock --- */}
      <div
        style={{
          position: 'fixed',
          bottom: 22,
          right: 76,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          background: 'rgba(15, 17, 23, 0.90)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          border: isRecording
            ? '1px solid rgba(239, 68, 68, 0.7)'
            : '1px solid rgba(255, 255, 255, 0.15)',
          borderRadius: 999,
          padding: '6px 14px',
          color: '#f4f4f5',
          fontSize: 13,
          boxShadow: isRecording
            ? '0 12px 35px rgba(239, 68, 68, 0.4), inset 0 1px 1px rgba(255, 255, 255, 0.25)'
            : '0 12px 35px rgba(0, 0, 0, 0.6), inset 0 1px 1px rgba(255, 255, 255, 0.15)',
          transition: 'all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      >
        {/* Item 1: Mic Toggle Button */}
        <button
          onClick={isRecording ? stopRecording : startRecording}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: isRecording
              ? 'rgba(239, 68, 68, 0.2)'
              : 'rgba(255, 255, 255, 0.08)',
            border: isRecording
              ? '1px solid rgba(239, 68, 68, 0.6)'
              : '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: 999,
            padding: '6px 14px',
            color: '#fff',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 600,
            transition: 'all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
          title={isRecording ? 'Bấm để dừng ghi âm' : 'Bấm để bắt đầu ghi âm bài giảng (Groq STT)'}
        >
          {isRecording ? (
            <>
              {/* Pulsing Beacon */}
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: '#ef4444',
                  animation: 'rbPulseRing 1.4s infinite ease-out',
                }}
              />
              {/* Equalizer Sound Waves (React Bits Sound Bar) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 2, height: 16 }}>
                <span style={{ width: 3, background: '#ef4444', borderRadius: 2, animation: 'rbWaveBar1 0.6s infinite ease-in-out' }} />
                <span style={{ width: 3, background: '#f87171', borderRadius: 2, animation: 'rbWaveBar2 0.7s infinite ease-in-out' }} />
                <span style={{ width: 3, background: '#fca5a5', borderRadius: 2, animation: 'rbWaveBar3 0.5s infinite ease-in-out' }} />
                <span style={{ width: 3, background: '#ef4444', borderRadius: 2, animation: 'rbWaveBar4 0.8s infinite ease-in-out' }} />
              </div>
              <span style={{ color: '#fca5a5' }}>{formatTime(recordSeconds)}</span>
              <span>Dừng</span>
            </>
          ) : (
            <>
              <span>🎙️</span>
              <span>Ghi âm bài giảng</span>
            </>
          )}
        </button>

        {/* Item 2: Download Audio Button (Solves: Where does the audio go after?) */}
        {lastAudioUrl && (
          <a
            href={lastAudioUrl}
            download={`bai_giang_mai_quyen_${new Date().toISOString().slice(0, 10)}.webm`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: 'rgba(59, 130, 246, 0.18)',
              border: '1px solid rgba(59, 130, 246, 0.45)',
              borderRadius: 999,
              padding: '6px 12px',
              color: '#93c5fd',
              fontSize: 12,
              fontWeight: 600,
              textDecoration: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            title="Tải toàn bộ file âm thanh bài giảng đã ghi (.webm) về máy tính"
          >
            <span>💾</span>
            <span>Tải Audio ({lastAudioSize})</span>
          </a>
        )}

        {/* Item 3: Ask Cáo Ơi Modal Toggle */}
        <button
          onClick={() => setShowAiModal(s => !s)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            background: showAiModal
              ? 'linear-gradient(135deg, rgba(168, 85, 247, 0.4) 0%, rgba(236, 72, 153, 0.4) 100%)'
              : 'rgba(255, 255, 255, 0.08)',
            border: showAiModal
              ? '1px solid rgba(168, 85, 247, 0.6)'
              : '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: 999,
            padding: '6px 12px',
            color: '#fff',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 600,
            transition: 'all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
          title="Mở bảng hỏi đáp Cáo Ơi (React Bits AI)"
        >
          <span>✨</span>
          <span
            style={{
              background: 'linear-gradient(90deg, #c084fc, #f472b6)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Hỏi Cáo Ơi
          </span>
        </button>

        {/* Status / Indicator */}
        {(isTranscribing || statusMessage) && (
          <span style={{ fontSize: 11, color: '#38bdf8', padding: '0 4px' }}>
            ⚡ {statusMessage || 'Đang dịch...'}
          </span>
        )}
      </div>
    </>
  );
};

export const WorkbenchRoot = memo(() => {
  const workbench = useService(WorkbenchService).workbench;

  // for debugging
  (window as any).workbench = workbench;

  const views = useLiveData(workbench.views$);

  const location = useLocation();
  const basename = location.pathname.match(/\/workspace\/[^/]+/g)?.[0] ?? '/';

  useAdapter(workbench, basename);

  const panelRenderer = useCallback((view: View) => {
    return <WorkbenchView view={view} />;
  }, []);

  const onMove = useCallback(
    (from: number, to: number) => {
      workbench.moveView(from, to);
    },
    [workbench]
  );

  useEffect(() => {
    workbench.updateBasename(basename);
  }, [basename, workbench]);

  return (
    <ViewIslandRegistryProvider>
      <SplitView
        className={styles.workbenchRootContainer}
        views={views}
        renderer={panelRenderer}
        onMove={onMove}
      />
      <WorkbenchSidebar />
      <GlobalVoicePillHeader />
    </ViewIslandRegistryProvider>
  );
});

WorkbenchRoot.displayName = 'memo(WorkbenchRoot)';

const WorkbenchView = ({ view }: { view: View }) => {
  const workbench = useService(WorkbenchService).workbench;

  const handleOnFocus = useCallback(() => {
    workbench.active(view);
  }, [workbench, view]);

  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (containerRef.current) {
      const element = containerRef.current;
      element.addEventListener('pointerdown', handleOnFocus, {
        capture: true,
      });
      return () => {
        element.removeEventListener('pointerdown', handleOnFocus, {
          capture: true,
        });
      };
    }
    return;
  }, [handleOnFocus]);

  return (
    <div className={styles.workbenchViewContainer} ref={containerRef}>
      <ViewRoot routes={routes} key={view.id} view={view} />
    </div>
  );
};

const MIN_SIDEBAR_WIDTH = 320;
const MAX_SIDEBAR_WIDTH = 1400;

const WorkbenchSidebar = () => {
  const { clientBorder } = useAtomValue(appSettingAtom);

  const [resizing, setResizing] = useState(false);

  const workbench = useService(WorkbenchService).workbench;
  const sidebarWidth = useLiveData(workbench.sidebarWidth$);
  const [width, setWidth] = useState(workbench.sidebarWidth$.value ?? 0);

  const views = useLiveData(workbench.views$);
  const activeView = useLiveData(workbench.activeView$);
  const sidebarOpen = useLiveData(workbench.sidebarOpen$);
  const [floating, setFloating] = useState(false);

  const onWidthChanged = useCallback(
    (width: number) => {
      workbench.setSidebarWidth(width);
      setWidth(width);
    },
    [workbench]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        workbench.openSidebar();
      } else {
        workbench.closeSidebar();
      }
    },
    [workbench]
  );

  useEffect(() => {
    const onResize = () => setFloating(!!(window.innerWidth < 768));
    onResize();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    if (resizing) return;
    setWidth(sidebarWidth ?? 0);
  }, [resizing, sidebarWidth]);

  return (
    <ResizePanel
      floating={floating}
      resizeHandlePos="left"
      resizeHandleOffset={clientBorder && sidebarOpen ? 3 : 0}
      width={width}
      resizing={resizing}
      onResizing={setResizing}
      className={styles.workbenchSidebar}
      data-client-border={clientBorder && sidebarOpen}
      open={sidebarOpen ?? false}
      onOpen={handleOpenChange}
      onWidthChange={setWidth}
      onWidthChanged={onWidthChanged}
      minWidth={MIN_SIDEBAR_WIDTH}
      maxWidth={MAX_SIDEBAR_WIDTH}
      unmountOnExit={false}
    >
      {views.map(view => (
        <FrameworkScope key={view.id} scope={view.scope}>
          <SidebarContainer
            style={{ display: activeView !== view ? 'none' : undefined }}
          />
        </FrameworkScope>
      ))}
    </ResizePanel>
  );
};
